/**
 * HeroBlock
 * @module components/HeroBlock
 */

import clsx from 'clsx';
import { useState, useRef } from 'react';
import Button from 'components/ui/Button';
import { HeroBlockModel } from 'types/block-types';
import { ButtonColorTypes } from 'types/common';
import Icon from 'components/ui/Icon';
import { useMedia } from 'hooks/useMedia';

interface HeroBlockProps extends HeroBlockModel {
	isInsideCarousel?: boolean;
}

const HeroBlock = ({
	heading,
	text,
	buttons,
	media,
	isInsideCarousel,
}: HeroBlockProps) => {
	const [canPlay, setCanPlay] = useState(false);
	const [isPlaying, setIsPlaying] = useState(true);
	const videoRef = useRef<HTMLVideoElement>(null);
	const isMobile = useMedia('(max-width: 767px)');

	const image = media?.image;
	const video = media?.video;
	const hasSizes = image?.sizes && image.sizes.length > 0;
	const hasWebpSizes = image?.webpSizes && image.webpSizes.length > 0;
	const isFullBleed =
		(image &&
			media?.imagePreference === 'fullWidthBackgroundImageFullHeight') ||
		(video && media?.videoPreference === 'videoFull');
	const isStartPage = window.location.pathname === '/';

	const handlePlayPauseClick = () => {
		const videoElement = videoRef.current;
		if (videoElement) {
			if (videoElement.paused) {
				videoElement.play();
				setIsPlaying(true);
			} else {
				videoElement.pause();
				setIsPlaying(false);
			}
		}
	};

	const getIframeSrc = () => {
		if (
			window &&
			window.CookieInformation &&
			window.CookieInformation.getConsentGivenFor &&
			window.CookieInformation.getConsentGivenFor('cookie_cat_marketing')
		) {
			return `${media?.video?.link}${
				media?.video?.autoplay ? '?autoplay=1&mute=1' : ''
			}`;
		} else {
			if (process.env.NODE_ENV === 'development') {
				return `${media?.video?.link}${
					media?.video?.autoplay ? '?autoplay=1&mute=1' : ''
				}`;
			} else {
				return '';
			}
		}
	};

	return (
		<section
			className={clsx(
				'relative  w-full  my-8 ',
				isFullBleed
					? 'flex flex-col md:flex-row items-center'
					: 'grid md:grid-cols-12',

				isStartPage ? 'md:min-h-160 md:mt-6' : 'md:min-h-115 md:mt-0',
				isInsideCarousel ? 'md:mb-0' : isStartPage ? 'md:mb-8' : 'md:mb-6'
			)}
		>
			<div
				className={clsx(
					'inset-0 w-full h-full hero-shape overflow-hidden min-h-65 ',
					isFullBleed ? 'relative md:absolute' : 'relative col-span-7 '
				)}
			>
				{image && (
					<picture className="absolute inset-0 w-full h-full px-4 md:px-0 ">
						{hasWebpSizes && (
							<source
								type="image/webp"
								srcSet={`${image!.webpSizes![0]} 1600w, ${
									image.webpSizes![1]
								} 1200w, ${image.webpSizes![2]} 800w, ${
									image.webpSizes![3]
								} 400w`}
								sizes="100vw"
							/>
						)}
						<img
							className="w-full h-full object-cover object-center rounded-card "
							src={image.src}
							srcSet={
								hasSizes
									? `${image.sizes![0]} 1600w, ${image.sizes![1]} 1200w, ${
											image.sizes![2]
									  } 800w, ${image.sizes![3]} 400w`
									: undefined
							}
							sizes={hasSizes ? '100vw' : undefined}
							alt={image.alt ?? ''}
						/>
					</picture>
				)}
				{video && (
					<div
						className={clsx(
							'relative md:absolute inset-0 w-full h-full hero-shape overflow-hidden'
						)}
					>
						<div className="md:h-full w-full relative px-4 md:px-0">
							{media?.type === 'youtube' || media?.type === 'twentythree' ? (
								<iframe
									className="md:h-115 w-full object-cover object-center md:inset-0 h-47.5"
									src={getIframeSrc()}
									data-consent-src={`${video.link}${
										video.autoplay ? '?autoplay=1&mute=1' : ''
									}`}
									title={`${media.type} - ${heading}`}
									data-category-consent="cookie_cat_marketing"
									allowFullScreen
									frameBorder="0"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								></iframe>
							) : (
								<>
									{media?.videoPosterImage && (
										<img
											className={clsx(
												'w-full object-cover object-center absolute md:max-h-full max-h-45 md:h-115',
												canPlay ? 'animate-fadeOut' : ''
											)}
											src={media?.videoPosterImage}
											alt="placeholder loading video"
										/>
									)}
									<video
										ref={videoRef}
										playsInline
										className="w-full object-cover object-center md:max-h-full h-full rounded-card"
										autoPlay={video.autoplay}
										muted
										loop
										onCanPlayThrough={(e) => {
											setCanPlay(true);
											if (e.currentTarget && e.currentTarget.play) {
												e.currentTarget.play();
											}
										}}
									>
										<source src={video.link} type="video/mp4" />
									</video>
								</>
							)}
						</div>
					</div>
				)}
				{heading && !isFullBleed && !isMobile && (
					<div className="relative w-full h-full  px-8 z-10 flex items-center  ">
						<h1
							className={clsx(
								'text-h1 md:text-hero text-white font-bold mb-8 max-w-150 ml-20'
							)}
						>
							{heading}
						</h1>
					</div>
				)}

				{/* overlay */}
				<div
					className="hidden md:block absolute inset-0 bg-overlay rounded-card hero-shape overflow-hidden mx-4 md:mx-0"
					style={{ mixBlendMode: 'multiply' }}
				/>
				{video && media?.type !== 'youtube' && media?.type !== 'twentythree' && (
					<button
						onClick={handlePlayPauseClick}
						className="absolute left-4 z-20 text-beige bottom-10 left-10 hidden md:block focus:outline-none"
						aria-label={isPlaying ? 'Pause video' : 'Play video'}
					>
						{isPlaying ? <Icon icon="pause" /> : <Icon icon="play" />}
					</button>
				)}
			</div>

			<div
				className={clsx(
					'relative w-full h-full mx-auto max-w-site px-8 flex items-center',
					!isFullBleed && 'col-span-5'
				)}
			>
				<div
					className={clsx(
						'md:pb-0 my-8 md:my-20 w-full',
						isInsideCarousel ? 'pb-0' : 'pb-10'
					)}
				>
					{heading && (isFullBleed || isMobile) && (
						<h1
							className={clsx(
								'text-h1 md:text-hero text-black md:text-white font-bold mb-8 max-w-150'
							)}
						>
							{heading}
						</h1>
					)}
					{text && (
						<p
							className={clsx(
								' text-p  font-normal md:text-preambleLight mb-8 max-w-150',
								isFullBleed
									? 'text-black md:text-white'
									: 'text-black px-0 md:px-12'
							)}
						>
							{text}
						</p>
					)}
					<div
						className={clsx(
							'flex flex-wrap gap-3',
							!isFullBleed && 'px-0 md:px-12'
						)}
					>
						{buttons &&
							buttons.length > 0 &&
							buttons.map((btn) => {
								let color = btn.color as ButtonColorTypes;
								if (isFullBleed && isMobile) {
									if (color === 'white') color = 'blue';
									else if (color === 'whiteTransparent') color = 'grey';
								}
								return (
									<Button
										type="link"
										url={btn.link}
										target={btn.target}
										buttonColor={color}
										noMargin
									>
										{btn.text}
										<Icon icon="arrow" />
									</Button>
								);
							})}
					</div>
				</div>
			</div>
		</section>
	);
};

export default HeroBlock;
